import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { onboardingApi, AdProfilePromptStatus } from '../services/api';
import { AdvertisingProfileForm } from './AdvertisingProfileForm';
import {
  isAdProfilePromptDismissedThisSession,
  markAdProfilePromptDismissedThisSession,
} from '../utils/adProfilePromptSession';

export const AdProfileOnboardingModal: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<AdProfilePromptStatus | null>(null);
  const [view, setView] = useState<'explain' | 'form'>('explain');
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Un report ("Plus tard") ne change pas l'état "required" côté backend
    // (le compteur est distinct de l'obligation) : c'est ce flag de session
    // qui évite à la modal de réapparaître à chaque navigation tant que
    // l'utilisateur ne s'est pas reconnecté (cf. AuthContext.logout).
    if (!user || location.pathname === '/upgrade-plan' || isAdProfilePromptDismissedThisSession()) {
      return;
    }
    onboardingApi.getAdProfileStatus()
      .then((response) => setStatus(response.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname]);

  if (!status || !status.required) {
    return null;
  }

  const handleDismiss = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      await onboardingApi.dismissAdProfilePrompt();
      markAdProfilePromptDismissedThisSession();
      setStatus(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Impossible d\'enregistrer votre choix, veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscoverPremium = () => {
    navigate('/account', { state: { activeTab: 'subscription' } });
  };

  const handleFormSuccess = () => {
    setStatus(null);
    refreshUser();
  };

  return (
    <div className="modal-overlay">
      <div className="modal ad-profile-modal">
        <h2>Continuez à profiter gratuitement de Ventilio</h2>

        {/* Le formulaire (centres d'intérêt + PCS + âge + sexe) peut dépasser la hauteur de
            l'écran : cette zone défile verticalement plutôt que la modal elle-même, qui reste
            contrainte au viewport (cf. .ad-profile-modal-body dans index.css). */}
        <div className="ad-profile-modal-body">
          {view === 'explain' ? (
            <>
              <p>
                Ventilio vous permet d'accéder gratuitement à ses fonctionnalités essentielles.
                Pour nous aider à maintenir et améliorer cette offre gratuite, nous vous invitons
                à compléter quelques informations.
              </p>
              <p>
                Ces informations contribuent au modèle économique de Ventilio et nous permettent
                de continuer à proposer une version gratuite du service.
              </p>
              <p>
                {status.mandatory
                  ? 'Vous avez déjà reporté cette étape à plusieurs reprises. Pour continuer à utiliser Ventilio, veuillez maintenant compléter le formulaire ou choisir une offre Premium.'
                  : 'Vous pouvez compléter le formulaire maintenant, découvrir nos offres Premium, ou reporter cette étape pour le moment.'}
              </p>

              {actionError && <div className="error-message">{actionError}</div>}

              <div className="modal-actions">
                <button className="btn btn-primary" onClick={() => setView('form')} disabled={submitting}>
                  Compléter le formulaire
                </button>
                <button className="btn btn-secondary" onClick={handleDiscoverPremium} disabled={submitting}>
                  Découvrir Premium
                </button>
                {!status.mandatory && (
                  <button className="btn btn-secondary" onClick={handleDismiss} disabled={submitting}>
                    Plus tard
                  </button>
                )}
              </div>
            </>
          ) : (
            <AdvertisingProfileForm onSuccess={handleFormSuccess} onCancel={() => setView('explain')} />
          )}
        </div>
      </div>
    </div>
  );
};
